// ─────────────────────────────────────────────────────────────────────────────
// utils/jobCounts.ts — Kennzahlen unabhaengig vom Live-Fenster (T-M10.3)
//
// REQ-OPS-034 (Kennzahlen und Abzeichen werden SERVERSEITIG gezaehlt, nie aus
// einem Live-Fenster abgeleitet; wo nicht gezaehlt wird, ist die Zahl als
// UNTERGRENZE zu kennzeichnen), REQ-OPS-033 (die Auftragsliste blaettert, sie
// kuerzt nicht), REQ-PS-021 (Live-Ueberwachung ist Kernzweck), REQ-DM-004
// (eigene Warteschlange je Drucker), REQ-DM-050/050a (Index-Deckung, statisch
// nachgewiesen), REQ-OV-002 (Statuswerte technisch-englisch; Uebersetzung nur
// in der Oberflaeche).
//
// ── WORUM ES GEHT ────────────────────────────────────────────────────────────
// Seit T-M9.3 haelt der `printJobStore` nur noch die ERSTE Seite live
// (`JOBS_PAGE_SIZE = 50`, vorher 200). Fuer die Auftragsliste ist das richtig —
// dort wird nachgeladen statt gekuerzt. Der Store ist aber GETEILT: die fuenf
// Kennzahl-Kacheln der Uebersicht, das „pending"-Abzeichen der Navigation und
// die Warteschlangenzahl je Drucker in der Flottenansicht lasen dieselbe Liste
// und luden NIE nach. Sie zaehlten damit eine Stichprobe der 50 juengsten
// Auftraege des Mandanten.
//
// Warum das genau dort wiegt, wo man hinschaut: an einem Grossanlass mit rund
// 20 Bestellungen je Minute ist ein Fenster von 50 Auftraegen zweieinhalb
// Minuten breit. Staut sich eine Warteschlange auf die Planungsgroesse von 200
// Arbeitsbons, fallen die AELTESTEN — die dringendsten — aus dem Fenster. Im
// Extremfall zeigt das Abzeichen fuer einen GESTAUTEN Drucker
// „Warteschlange leer".
//
// ── WAS DIESES MODUL TUT ─────────────────────────────────────────────────────
//   • Es beschreibt die Zaehl-Abfragen als DATEN (`JobCountSpec`) statt als
//     Firestore-Aufrufe. Dieselbe Beschreibung wird an zwei Orten verwendet:
//     `services/printJobService.ts` uebersetzt sie in `getCountFromServer(...)`,
//     und `test/firestoreIndexes.test.ts` prueft ihre Index-Deckung gegen
//     `firebase/firestore.indexes.json` (REQ-DM-050a).
//   • Es wertet dieselbe Beschreibung ueber einem Datensatz aus
//     (`countMatching`) — genau das, was `count()` serverseitig tut. Damit ist
//     die Zaehlung ohne Emulator und ohne Mount falsifizierbar: derselbe
//     Datensatz, einmal ueber das Live-Fenster gezaehlt und einmal ueber die
//     Beschreibung, muss auseinanderfallen, sobald mehr als eine Seite vorliegt.
//   • Es kennzeichnet, was NICHT gezaehlt ist (`resolveCount` → `exact: false`).
//     Eine aus einer begrenzten Liste abgeleitete Zahl darf nicht als exakte
//     Zahl erscheinen (REQ-OPS-034, zweiter Satz).
//   • Es haelt den Takt (`createRefreshTimer`) — mit einspritzbaren Zeitgebern,
//     damit „der Timer wird abgeraeumt" pruefbar ist statt behauptet.
//
// ── WAS DIESES MODUL AUSDRUECKLICH NICHT TUT ────────────────────────────────
//   • Es kennt Firestore nicht. Keine Abfrage, kein `where`, kein SDK-Import —
//     die Uebersetzung in Firestore-Bedingungen liegt im Service.
//   • Es ruehrt `subscribeToPrinterQueue` nicht an. Diese Abfrage traegt GAR
//     KEIN `limit` und filtert serverseitig auf `printerId` + `status`; die
//     aufgeklappte Warteschlange war und ist vollstaendig. Falsch war nur das
//     ABZEICHEN daneben (REQ-OPS-034, Gegenprobe).
//   • Es aendert die Seitengroesse und die Pagination aus T-M9.3 nicht.
//
// ── NACHTRAG T-M15.6: EIN GEHALTENER AUFTRAG IST KEIN STAU (REQ-LC-015) ─────
// Ein zurueckgehaltener Arbeitsbon (`holdUntilReady == true`, REQ-LC-009) steht
// auf `pending` und zaehlte damit in jede dieser Zahlen mit. Ein Betreiber saehe
// einen Stau, den es nicht gibt — und der echte Stau ginge darin unter. Jede
// Zaehlstelle nimmt ihn deshalb heraus ODER weist ihn als eigene, benannte Menge
// aus („zurueckgehalten"); stillschweigend mitzaehlen ist unzulaessig.
//
// DIE RICHTUNG, IN DER GEZAEHLT WIRD, IST DABEI VORGESCHRIEBEN und steht an
// EINER Stelle: `HELD_EQUALITY_VALUE` / `subtractHeld`. Dort ist auch begruendet,
// warum ein direkter Filter `holdUntilReady == false` genau den Befund
// zurueckbraechte, gegen den REQ-OPS-034 gebaut ist.
//
// Reine Funktionen ohne Vue-, i18n- und Firestore-Abhaengigkeit. Rueckgabewerte
// sind Zahlen, Wahrheitswerte und i18n-SCHLUESSEL — nie fertiger Text.
// ─────────────────────────────────────────────────────────────────────────────

import { PrintJobStatus } from '@/firebase/models';
import { PRINT_JOBS_COLLECTION } from '@/firebase/collections';

// ── Takt ─────────────────────────────────────────────────────────────────────

/**
 * Erneuerungstakt der Zaehlungen: 30 Sekunden.
 *
 * `count()` ist eine Momentaufnahme, kein Abo — anders als `onSnapshot` meldet
 * es keine Veraenderung von sich aus. Die Groessenordnung ist bewusst dieselbe
 * wie beim Lebenszeichen der Druckserver: wer die Flotte im Stoerfall liest,
 * darf keine minutenalte Zahl vor sich haben.
 */
export const JOB_COUNT_REFRESH_MS = 30_000;

/**
 * Mindestabstand zweier Zaehl-Laeufe, wenn die LIVE-SEITE sie ausloest.
 *
 * Die Zahlen werden zusaetzlich zum Takt erneuert, sobald sich die Live-Seite
 * veraendert — sonst stuende eine gerade eingetroffene Bestellung bis zu 30
 * Sekunden nicht in der Kennzahl. An einem Grossanlass trifft aber alle drei
 * Sekunden ein Auftrag ein; ohne Mindestabstand entstuende je Auftrag ein
 * Buendel von Zaehl-Abfragen.
 *
 * Ein ausgelassener Lauf ist NICHT verloren: der Takt holt ihn spaetestens nach
 * `JOB_COUNT_REFRESH_MS` ein — die Anforderung verlangt „spaetestens im
 * festgelegten Takt", nicht „sofort".
 */
export const JOB_COUNT_MIN_GAP_MS = 5_000;

// ── Beschreibung einer Zaehl-Abfrage ─────────────────────────────────────────

/**
 * Ein Gleichheitsfilter (`where(field, '==', value)`).
 *
 * `value` ist seit T-M15.6 `string | boolean`. Vorher war es ausschliesslich
 * `string` — und damit liess sich die einzige Bedingung, um die es in
 * REQ-LC-015 geht, gar nicht ausdruecken: `holdUntilReady` ist ein
 * WAHRHEITSWERT (REQ-DM-030). Firestore vergleicht typstreng; die Zeichenkette
 * `'true'` traefe kein einziges Dokument und lieferte ein leeres Ergebnis OHNE
 * Fehlermeldung — dieselbe Falle wie in REQ-OPS-040 (d).
 */
export interface CountEquality {
  field: string;
  value: string | boolean;
}

/**
 * Die Form einer Zaehl-Abfrage — bewusst als Daten und nicht als Firestore-Query.
 *
 * Sie traegt AUSSCHLIESSLICH Gleichheitsfilter und KEINE Sortierung: eine
 * Aggregation braucht keine Reihenfolge. Genau daran haengt die Index-Lage —
 * eine Abfrage ohne `orderBy` wird von jedem Index bedient, dessen Praefix ihre
 * Gleichheitsfelder enthaelt. Es entsteht deshalb KEINE neue Indexform
 * (REQ-OPS-034, Index-Lage; nachgewiesen in `test/firestoreIndexes.test.ts`).
 */
export interface JobCountSpec {
  /** Sprechende Kennung fuer Fehlermeldungen und die Registrierung im Waechter. */
  id:         string;
  collection: string;
  equality:   CountEquality[];
}

/** Kennungen der Zaehl-Abfragen — dieselben Zeichenketten wie im Index-Waechter. */
export const COUNT_SPEC_IDS = {
  total:            'jobs.count.total',
  status:           'jobs.count.status',
  printerQueue:     'jobs.count.printerQueue',
  // T-M15.6: die GEHALTENE Teilmenge, je Mandant und je Drucker.
  statusHeld:       'jobs.count.statusHeld',
  printerQueueHeld: 'jobs.count.printerQueueHeld',
} as const;

/**
 * Der Wert, mit dem die gehaltene Menge gezaehlt wird — `true`, nie `false`.
 *
 * ⚠ DIE RICHTUNG IST VORGESCHRIEBEN (REQ-LC-015 Praez. 1 (d)) und ist der Kern
 * dieses Tasks: gezaehlt wird `holdUntilReady == true`, und das Ergebnis wird
 * von der Gesamtzahl ABGEZOGEN. Ein direkter Filter `holdUntilReady == false`
 * waere naheliegend und falsch — ein Bestandsdokument OHNE das Feld erfuellt
 * KEINE Gleichheitsbedingung (Firestore kann nicht auf „Feld fehlt" abfragen,
 * REQ-LC-009). Es fiele damit aus der Zaehlung heraus, Kachel und Abzeichen
 * wuerden ZU KLEIN, und im Grenzfall stuende wieder „Warteschlange leer" an
 * einem gestauten Drucker — woertlich der Befund, gegen den REQ-OPS-034 gebaut
 * ist, auf einem neuen Weg.
 *
 * Mit der Differenz `alle pending − gehalten` zeigt der Fehler nach OBEN: ein
 * noch nicht nachgezogenes Dokument (T-M15.9) gilt weiterhin als wartend, die
 * Zahl ist hoechstens zu gross, und niemand liest eine Leermeldung, die nicht
 * stimmt.
 */
export const HELD_EQUALITY_VALUE = true;

/**
 * Die Kachel „gesamt": alle Auftraege des Mandanten.
 *
 * Ein einzelner Gleichheitsfilter — den bedient schon der automatische
 * Einzelfeld-Index auf `companyId`.
 */
export function totalJobCountSpec(companyId: string): JobCountSpec {
  return {
    id:         COUNT_SPEC_IDS.total,
    collection: PRINT_JOBS_COLLECTION,
    equality:   [{ field: 'companyId', value: companyId }],
  };
}

/**
 * Eine Kachel je Zustellstatus: `companyId` + `status`.
 *
 * Praefix des bestehenden Index `printJobs: companyId ASC, status ASC,
 * createdAt DESC` — derselbe, der die Auftragsliste mit Statusfilter traegt.
 */
export function statusJobCountSpec(companyId: string, status: PrintJobStatus): JobCountSpec {
  return {
    id:         COUNT_SPEC_IDS.status,
    collection: PRINT_JOBS_COLLECTION,
    equality:   [
      { field: 'companyId', value: companyId },
      { field: 'status',    value: status },
    ],
  };
}

/**
 * Das Warteschlangen-Abzeichen je Drucker: `companyId` + `printerId` +
 * `status == 'pending'` (REQ-DM-004).
 *
 * Dieselben Filter wie `subscribeToPrinterQueue` — nur ohne Sortierung. Praefix
 * des bestehenden Index `printJobs: companyId ASC, printerId ASC, status ASC,
 * createdAt ASC`, der jene Abfrage heute schon traegt.
 */
export function printerPendingCountSpec(companyId: string, printerId: string): JobCountSpec {
  return {
    id:         COUNT_SPEC_IDS.printerQueue,
    collection: PRINT_JOBS_COLLECTION,
    equality:   [
      { field: 'companyId', value: companyId },
      { field: 'printerId', value: printerId },
      { field: 'status',    value: PrintJobStatus.PENDING },
    ],
  };
}

/**
 * Die GEHALTENE Teilmenge der wartenden Auftraege eines Mandanten:
 * `companyId` + `status == 'pending'` + `holdUntilReady == true` (REQ-LC-015).
 *
 * Vier Gleichheitsfelder statt drei — und damit die erste Zaehlform, die der
 * Satz „es entsteht keine neue Indexform" (REQ-OPS-034) nicht mehr traegt. Sie
 * haengt am Eintrag `printJobs: companyId ASC, status ASC, holdUntilReady ASC`
 * (REQ-OPS-034 Nachtrag 2026-08-12, geprueft in `test/firestoreIndexes.test.ts`
 * Abschnitt 2b).
 */
export function heldStatusJobCountSpec(companyId: string): JobCountSpec {
  return {
    id:         COUNT_SPEC_IDS.statusHeld,
    collection: PRINT_JOBS_COLLECTION,
    equality:   [
      { field: 'companyId',      value: companyId },
      { field: 'status',         value: PrintJobStatus.PENDING },
      { field: 'holdUntilReady', value: HELD_EQUALITY_VALUE },
    ],
  };
}

/**
 * Dieselbe gehaltene Teilmenge fuer GENAU EINEN Drucker (REQ-LC-015, REQ-DM-004).
 *
 * Traegt der Eintrag `printJobs: companyId ASC, printerId ASC, status ASC,
 * holdUntilReady ASC`.
 */
export function printerHeldCountSpec(companyId: string, printerId: string): JobCountSpec {
  return {
    id:         COUNT_SPEC_IDS.printerQueueHeld,
    collection: PRINT_JOBS_COLLECTION,
    equality:   [
      { field: 'companyId',      value: companyId },
      { field: 'printerId',      value: printerId },
      { field: 'status',         value: PrintJobStatus.PENDING },
      { field: 'holdUntilReady', value: HELD_EQUALITY_VALUE },
    ],
  };
}

/**
 * Die Zustellstatus, fuer die es eine eigene Kachel gibt (REQ-OPS-034).
 *
 * `expired` ist bewusst NICHT dabei: die Uebersicht hat dafuer keine Kachel.
 * Kaeme eine hinzu, ist sie hier zu ergaenzen — der Waechter im Test haelt
 * Kacheln und Zaehlungen deckungsgleich.
 */
export const COUNTED_JOB_STATUSES: readonly PrintJobStatus[] = [
  PrintJobStatus.PENDING,
  PrintJobStatus.PRINTING,
  PrintJobStatus.COMPLETED,
  PrintJobStatus.FAILED,
];

// ── Auswertung derselben Beschreibung ueber einem Datensatz ──────────────────

/**
 * Der fuer die Zaehlung gelesene Teil eines `printJobs`-Dokuments.
 *
 * Bewusst OHNE Index-Signatur: sie zwaenge jeden Aufrufer, eine mitzubringen.
 * Die Felder werden ueber ihren Namen aus der Beschreibung gelesen, nicht ueber
 * eine feste Eigenschaft — das Feld steht in `JobCountSpec`, nicht im Typ.
 */
export interface CountableJob {
  companyId?: string;
  printerId?: string;
  status?:    string;
  /**
   * Das Halte-Kennzeichen (REQ-LC-009). AUSDRUECKLICH OPTIONAL, und das ist
   * keine Bequemlichkeit: ein Bestandsdokument von vor der Nachziehung
   * (T-M15.9) traegt das Feld NICHT. Genau dieser Fall muss darstellbar sein,
   * sonst laesst sich die Gegenprobe zu REQ-LC-015 Praez. 1 (d) gar nicht
   * schreiben — ein Dokument ohne das Feld zaehlt weiterhin als wartend.
   */
  holdUntilReady?: boolean;
}

/**
 * Erfuellt ein Auftrag alle Gleichheitsfilter der Beschreibung?
 *
 * Ein fehlendes Feld erfuellt keinen Filter — dieselbe Regel wie in Firestore:
 * ein Dokument ohne das gefilterte Feld steht nicht im Index und wird nie
 * geliefert.
 */
export function matchesCountSpec(job: CountableJob, spec: JobCountSpec): boolean {
  const fields = job as Record<string, unknown>;
  return spec.equality.every(f => fields[f.field] === f.value);
}

/**
 * Zaehlt einen Datensatz nach der Beschreibung — das, was `count()` auf dem
 * Server tut.
 *
 * Ausdruecklich OHNE Fenster, ohne Seite, ohne Grenze: die Funktion sieht alle
 * uebergebenen Auftraege. Der Test nutzt sie, um die serverseitige Zaehlung
 * gegen die aus dem Live-Fenster abgeleitete Zahl zu stellen.
 */
export function countMatching(
  jobs: readonly CountableJob[],
  spec: JobCountSpec,
): number {
  let n = 0;
  for (const job of jobs) if (matchesCountSpec(job, spec)) n += 1;
  return n;
}

// ── Die gehaltene Menge: zaehlen und ABZIEHEN (REQ-LC-015) ──────────────────

/**
 * Die wartende Menge aus Gesamt und Gehalten — die EINE Stelle, an der die
 * vorgeschriebene Richtung steht (REQ-LC-015 Praez. 1 (d)).
 *
 * Nie kleiner als null: schlagen die zwei Aggregationen mit einem Augenblick
 * Abstand zu, kann die gehaltene Zahl kurz groesser sein als die Gesamtzahl
 * (ein Auftrag wurde dazwischen freigegeben). Eine negative Warteschlange ist
 * keine Aussage.
 */
export function subtractHeld(allPending: number, held: number): number {
  const alle     = Math.max(0, Math.trunc(allPending));
  const gehalten = Math.max(0, Math.trunc(held));
  return Math.max(0, alle - gehalten);
}

/**
 * Dieselbe Rechnung ueber einem Datensatz — das, was die zwei `count()`-Aufrufe
 * serverseitig tun.
 *
 * Sie ist der Nachweis-Anker fuer Kriterium 6: derselbe Datensatz, darin ein
 * `pending`-Dokument OHNE das Feld, muss weiterhin als wartend gezaehlt werden.
 * Wer die Zaehlung auf einen direkten Filter `holdUntilReady == false`
 * umstellte, saehe genau hier die zu kleine Zahl.
 */
export function countWaiting(
  jobs: readonly CountableJob[],
  allSpec: JobCountSpec,
  heldSpec: JobCountSpec,
): number {
  return subtractHeld(countMatching(jobs, allSpec), countMatching(jobs, heldSpec));
}

/**
 * Kann an diesem Ausgabekanal ueberhaupt ein Auftrag zurueckgehalten werden?
 *
 * Genau `kitchenTvEnabled === true` — Bedingung (3) aus REQ-LC-009. Daran
 * haengt die ausdruecklich gebilligte EINSPARUNG aus REQ-LC-015 Praez. 1 (d):
 * ist die Antwort `false`, entfaellt die zweite Aggregation je Drucker, weil
 * sie dort nur null liefern koennte. Ohne diese Einsparung waeren es bei einem
 * Mandanten nahe `PRINTER_LIST_LIMIT = 100` bis zu 100 WEITERE Aggregationen je
 * 30-Sekunden-Takt und geoeffneter Flottenansicht (OP-82).
 *
 * Streng `=== true`: ein fehlendes Feld und jeder Fremdtyp heissen „nicht
 * zugeschaltet". Die Richtung ist die schadensaermere — waere ein Kanal
 * faelschlich als haltefaehig gelesen, entstuende bloss eine ueberfluessige
 * Aggregation mit dem Ergebnis null; umgekehrt bliebe ein gehaltener Auftrag
 * ungezaehlt und zaehlte still als Stau mit.
 */
export function canHoldJobs(printer: { kitchenTvEnabled?: boolean }): boolean {
  return printer.kitchenTvEnabled === true;
}

/**
 * Die wartende Zahl EINES Druckers aus den zwei Aggregationen — oder
 * `undefined`, wenn daraus keine ehrliche Zahl wird.
 *
 * Fehlt eine der beiden Zahlen, ist das Ergebnis `undefined` und die Ansicht
 * faellt auf die als Untergrenze gekennzeichnete Fensterzahl zurueck
 * (REQ-OPS-034 Praez. 2). Das gilt AUCH, wenn nur die gehaltene Zahl fehlt:
 * die Gesamtzahl dann als exakte Zahl zu zeigen hiesse, die gehaltenen
 * STILLSCHWEIGEND mitzuzaehlen — und genau das ist unzulaessig (REQ-LC-015).
 * Eine gekennzeichnete Untergrenze ist die ehrlichere Aussage.
 *
 * An einem Kanal ohne Halte-Moeglichkeit (`canHoldJobs === false`) wird die
 * zweite Aggregation gar nicht abgesetzt; der Aufrufer reicht dort `0` herein.
 */
export function waitingForPrinter(
  allPending: number | null | undefined,
  held: number | null | undefined,
): number | undefined {
  if (typeof allPending !== 'number' || !Number.isFinite(allPending)) return undefined;
  if (typeof held       !== 'number' || !Number.isFinite(held))       return undefined;
  return subtractHeld(allPending, held);
}

// ── Die gehaltenen Auftraege einer Warteschlange ─────────────────────────────

/** Der fuer die Halte-Frage gelesene Teil eines Auftrags. */
export interface HoldableJob {
  holdUntilReady?: boolean | null;
}

/**
 * Wird dieser Auftrag zurueckgehalten?
 *
 * Streng `=== true`: ein fehlendes Feld und jeder Fremdtyp heissen „nicht
 * gehalten" — dieselbe Richtung wie bei der Zaehlung (REQ-LC-015 Praez. 1 (d)).
 * Ein Bestandsdokument ohne das Feld erscheint damit unter den WARTENDEN, wo
 * es hingehoert, und verschwindet nicht aus der Liste.
 */
export function isHeldJob(job: HoldableJob): boolean {
  return job.holdUntilReady === true;
}

/**
 * Eine Warteschlange in wartend und zurueckgehalten trennen — unter Beibehaltung
 * der Reihenfolge, in der sie hereinkam (FIFO nach `createdAt`, REQ-LC-011).
 *
 * Wozu: die aufgeklappte Warteschlange zaehlt zwar nicht, VERGIBT ABER
 * FIFO-POSITIONEN und behauptet „Warteschlange leer". Ein gehaltener Auftrag
 * auf Position 1 ist woertlich „stillschweigend mitgezaehlt" (REQ-LC-015). Die
 * Abfrage `subscribeToPrinterQueue` bleibt dabei UNVERAENDERT — das Kennzeichen
 * liegt am Dokument und ist ohne Abfrageaenderung und ohne Index zu haben
 * (REQ-LC-015 Praez. 1 (b), REQ-OPS-034 Gegenprobe).
 */
export function partitionHeldJobs<T extends HoldableJob>(
  jobs: readonly T[],
): { waiting: T[]; held: T[] } {
  const waiting: T[] = [];
  const held:    T[] = [];
  for (const job of jobs) (isHeldJob(job) ? held : waiting).push(job);
  return { waiting, held };
}

// ── Gezaehlt oder Untergrenze ────────────────────────────────────────────────

/**
 * Eine Zahl SAMT der Auskunft, ob sie exakt ist.
 *
 * Der Wahrheitswert ist kein Beiwerk, sondern der Kern von REQ-OPS-034: eine
 * aus einer begrenzten Liste abgeleitete Zahl darf nicht als exakte Zahl
 * erscheinen. Wer nur `value` liest und `exact` wegwirft, baut den Befund
 * wieder ein.
 */
export interface CountedNumber {
  value: number;
  exact: boolean;
}

/** Eine serverseitig gezaehlte, exakte Zahl. */
export function exactCount(value: number): CountedNumber {
  return { value: Math.max(0, Math.trunc(value)), exact: true };
}

/**
 * Eine Zahl, die aus einer begrenzten Liste stammt: sie ist eine UNTERGRENZE.
 *
 * Mehr laesst sich ueber ein Fenster nicht sagen — was dahinter liegt, hat die
 * Liste nie gesehen.
 */
export function lowerBoundCount(value: number): CountedNumber {
  return { value: Math.max(0, Math.trunc(value)), exact: false };
}

/**
 * Die anzuzeigende Zahl: die serverseitige Zaehlung, wenn sie vorliegt —
 * sonst der Wert aus dem Live-Fenster, als Untergrenze gekennzeichnet.
 *
 * Der Rueckfall ist ausdruecklich gewollt: waehrend die erste Zaehlung laeuft
 * (und wenn sie fehlschlaegt) zeigt die Ansicht lieber eine ehrlich als
 * „mindestens" gekennzeichnete Zahl als gar keine. Eine als exakt gelesene, zu
 * kleine Zahl ist der schlechteste der Zustaende — genau deshalb gibt es diesen
 * Task.
 */
export function resolveCount(
  serverCount: number | null | undefined,
  windowFallback: number,
): CountedNumber {
  if (typeof serverCount === 'number' && Number.isFinite(serverCount)) {
    return exactCount(serverCount);
  }
  return lowerBoundCount(Number.isFinite(windowFallback) ? windowFallback : 0);
}

/**
 * Die Kennzahlen der Uebersicht, je mit ihrer Exaktheits-Auskunft.
 *
 * ⚠ `pending` ist seit T-M15.6 die WARTENDE Menge OHNE die zurueckgehaltenen
 * Auftraege; die gehaltenen stehen als eigene, benannte Menge daneben
 * (`pendingHeld`, Beschriftung „zurueckgehalten"). Das ist die WEITERE der zwei
 * zulaessigen Formen aus REQ-LC-015 — und sie gilt hier, weil die Uebersicht
 * der eine Ort ist, an dem „was liegt insgesamt an" gefragt wird: die
 * gehaltenen Auftraege dort ERSATZLOS verschwinden zu lassen waere
 * Informationsverlust statt Klarheit (REQ-LC-015 Praez. 1 (b)).
 */
export interface CountedJobStats {
  total:       CountedNumber;
  pending:     CountedNumber;
  /** Die gesondert ausgewiesene, benannte Menge: „zurueckgehalten". */
  pendingHeld: CountedNumber;
  printing:    CountedNumber;
  completed:   CountedNumber;
  failed:      CountedNumber;
}

/**
 * Die roh gezaehlten Werte, wie sie der Server liefert.
 *
 * `pending` ist hier ALLES, was auf `pending` steht — die ungeschmaelerte
 * Aggregation. Der Abzug geschieht erst in `resolveJobStats`, und zwar an
 * genau einer Stelle (`subtractHeld`).
 */
export interface JobStatCounts {
  total:       number;
  pending:     number;
  /** `pending` UND `holdUntilReady == true` (REQ-LC-015 Praez. 1 (d)). */
  pendingHeld: number;
  printing:    number;
  completed:   number;
  failed:      number;
}

/**
 * Die Kacheln aus Zaehlung und Fenster zusammensetzen.
 *
 * Alles oder nichts je Feld: liegt die Zaehlung vor, ist die Kachel exakt;
 * fehlt sie, ist sie Untergrenze. Es wird nie gemischt („total gezaehlt,
 * pending geschaetzt") — das waere fuer den Leser nicht auseinanderzuhalten.
 *
 * Der Abzug greift auf BEIDEN Wegen (REQ-LC-015 Praez. 1 (a) Zaehlstelle 5):
 * die gezaehlte Kachel zeigt `pending − gehalten`, die Rueckfallzahl aus dem
 * Fenster ebenso. Zoege nur eine der beiden ab, waere die als „≥ n"
 * gekennzeichnete Untergrenze GROESSER als die exakte Zahl daneben — der Leser
 * saehe einen Widerspruch statt einer Untergrenze.
 */
export function resolveJobStats(
  counted: JobStatCounts | null | undefined,
  windowStats: JobStatCounts,
): CountedJobStats {
  const countedWaiting = counted
    ? subtractHeld(counted.pending, counted.pendingHeld)
    : undefined;

  return {
    total:       resolveCount(counted?.total, windowStats.total),
    pending:     resolveCount(
      countedWaiting,
      subtractHeld(windowStats.pending, windowStats.pendingHeld),
    ),
    pendingHeld: resolveCount(counted?.pendingHeld, windowStats.pendingHeld),
    printing:    resolveCount(counted?.printing,  windowStats.printing),
    completed:   resolveCount(counted?.completed, windowStats.completed),
    failed:      resolveCount(counted?.failed,    windowStats.failed),
  };
}

// ── i18n-Schluessel (nie fertiger Text, REQ-OV-002) ──────────────────────────

/** Kennzeichnung einer Untergrenze in Langform: „mindestens {count}". */
export const COUNT_AT_LEAST_KEY = 'common.count.atLeast';
/** Kennzeichnung einer Untergrenze in Kurzform fuer Abzeichen: „≥ {count}". */
export const COUNT_AT_LEAST_SHORT_KEY = 'common.count.atLeastShort';
/** Erlaeuterung, warum eine Zahl nur eine Untergrenze ist. */
export const COUNT_AT_LEAST_HINT_KEY = 'common.count.atLeastHint';

/**
 * Der i18n-Schluessel fuer eine Kennzahl — oder `null`, wenn die Zahl exakt ist
 * und einfach als Zahl erscheinen darf.
 */
export function countLabelKey(counted: CountedNumber): string | null {
  return counted.exact ? null : COUNT_AT_LEAST_KEY;
}

// ── Beschriftung der gehaltenen Menge (REQ-LC-015, REQ-OV-002/005) ──────────
//
// Deutsche Beschriftung „zurueckgehalten" in der Oberflaeche; der Wert in
// Firestore bleibt technisch-englisch (`holdUntilReady`). Wie ueberall in
// diesem Modul stehen hier SCHLUESSEL, nie fertiger Text.

/** Kachel der Uebersicht: die gesondert ausgewiesene, benannte Menge. */
export const HELD_LABEL_KEY = 'dashboard.heldJobs';
/** Warum sie neben der Kachel `pending` steht und nicht in ihr. */
export const HELD_HINT_KEY  = 'dashboard.heldJobsHint';
/** Ueberschrift der abgruppierten Eintraege in der aufgeklappten Warteschlange. */
export const QUEUE_HELD_HEADING_KEY = 'fleet.queueHeldHeading';
/** Erlaeuterung dort — warum diese Eintraege keine FIFO-Position tragen. */
export const QUEUE_HELD_HINT_KEY    = 'fleet.queueHeldHint';
/** „Keine wartenden Auftraege" — gesagt, wenn NUR gehaltene daliegen. */
export const QUEUE_NONE_WAITING_KEY = 'fleet.queueNoneWaiting';

// ── Das Warteschlangen-Abzeichen je Drucker ──────────────────────────────────

/**
 * Was das Abzeichen neben einem Drucker aussagt.
 *
 *   • `empty`   — gezaehlt und null: die Warteschlange ist wirklich leer.
 *   • `exact`   — gezaehlt und groesser null: so viele warten.
 *   • `atLeast` — nicht gezaehlt, aber im Fenster gesehen: MINDESTENS so viele.
 *   • `unknown` — nicht gezaehlt und im Fenster nichts gesehen. Daraus folgt
 *                 NICHTS. Genau hier stand frueher „Warteschlange leer" fuer
 *                 einen gestauten Drucker — die Aussage, gegen die dieser Task
 *                 gebaut ist.
 */
export type QueueBadgeKind = 'empty' | 'exact' | 'atLeast' | 'unknown';

export interface QueueBadge {
  kind:  QueueBadgeKind;
  count: number;
  /** i18n-Schluessel des anzuzeigenden Textes. */
  key:   string;
}

export const QUEUE_BADGE_KEYS: Record<QueueBadgeKind, string> = {
  empty:   'fleet.queueEmpty',
  exact:   'fleet.pending',
  atLeast: 'fleet.pendingAtLeast',
  unknown: 'fleet.queueUnknown',
};

/**
 * Die Aussage des Abzeichens aus der Zahl herleiten.
 *
 * Die eine Regel, auf die es ankommt: „leer" wird NUR behauptet, wenn
 * serverseitig gezaehlt wurde. Eine Untergrenze von null ist keine Leermeldung,
 * sondern eine Nichtaussage.
 */
export function queueBadge(counted: CountedNumber): QueueBadge {
  const count = Math.max(0, Math.trunc(counted.value));
  let kind: QueueBadgeKind;
  if (counted.exact) kind = count > 0 ? 'exact' : 'empty';
  else               kind = count > 0 ? 'atLeast' : 'unknown';
  return { kind, count, key: QUEUE_BADGE_KEYS[kind] };
}

// ── Takt: erneuern und abraeumen ─────────────────────────────────────────────

/** Einspritzbare Zeitgeber — im Test durch Zaehler ersetzbar. */
export interface TimerHost {
  setInterval:   (fn: () => void, ms: number) => unknown;
  clearInterval: (handle: unknown) => void;
  now:           () => number;
}

const REAL_TIMERS: TimerHost = {
  setInterval:   (fn, ms) => setInterval(fn, ms),
  clearInterval: (handle) => clearInterval(handle as ReturnType<typeof setInterval>),
  now:           () => Date.now(),
};

export interface RefreshTimer {
  /** Laeuft der Takt? */
  readonly running: boolean;
  /** Startet den Takt und fuehrt sofort einen ersten Lauf aus. */
  start(): void;
  /** Raeumt den Takt ab. Mehrfach aufrufbar. */
  stop(): void;
  /**
   * Ausserplanmaessiger Lauf (die Live-Seite hat sich veraendert). Wird
   * ausgelassen, wenn der letzte Lauf weniger als `minGapMs` zurueckliegt.
   *
   * @returns ob der Lauf stattgefunden hat.
   */
  poke(): boolean;
}

export interface RefreshTimerOptions {
  intervalMs?: number;
  minGapMs?:   number;
  timers?:     TimerHost;
}

/**
 * Ein Takt, der sich abraeumen laesst — und dessen Abraeumen pruefbar ist.
 *
 * Warum das nicht einfach `setInterval` in der Ansicht ist: „kein Timer bleibt
 * nach dem Verlassen der Ansicht stehen" ist ein Akzeptanzkriterium, und ein
 * stehengebliebener Timer ist ein Befund, kein Detail. Mit einspritzbaren
 * Zeitgebern laesst sich genau das belegen, statt es zu behaupten.
 *
 * `start()` ist gegen Doppelstart geschuetzt: ein zweiter Aufruf raeumt den
 * vorigen Takt zuerst ab. Sonst entstuende bei jedem Mandantenwechsel ein
 * zusaetzlicher, nie abgeraeumter Zeitgeber.
 */
export function createRefreshTimer(
  run: () => void,
  options: RefreshTimerOptions = {},
): RefreshTimer {
  const intervalMs = options.intervalMs ?? JOB_COUNT_REFRESH_MS;
  const minGapMs   = options.minGapMs   ?? JOB_COUNT_MIN_GAP_MS;
  const timers     = options.timers     ?? REAL_TIMERS;

  let handle: unknown = null;
  let lastRunAt = Number.NEGATIVE_INFINITY;

  function execute(): void {
    lastRunAt = timers.now();
    run();
  }

  return {
    get running() { return handle !== null; },

    start(): void {
      this.stop();
      handle = timers.setInterval(execute, intervalMs);
      execute();
    },

    stop(): void {
      if (handle !== null) {
        timers.clearInterval(handle);
        handle = null;
      }
    },

    poke(): boolean {
      if (timers.now() - lastRunAt < minGapMs) return false;
      execute();
      return true;
    },
  };
}

/**
 * Darf ein Zaehl-Ergebnis noch angezeigt werden?
 *
 * `count()` ist asynchron. Wechselt der Mandant, waehrend eine Zaehlung laeuft,
 * trifft deren Antwort NACH dem Wechsel ein — und traegt die Zahl des vorigen
 * Mandanten. Sie ist zu verwerfen, sonst hallt sie in der neuen Ansicht nach
 * (Akzeptanzkriterium T-M10.3).
 */
export function shouldAcceptCounts(
  requestedCompanyId: string | null | undefined,
  currentCompanyId: string | null | undefined,
): boolean {
  return !!requestedCompanyId && requestedCompanyId === currentCompanyId;
}
